/**
 * heal-spa-ada-fixes.js
 * ──────────────────────────────────────────────────────────────────────────
 * Drop-in ADA / WCAG 2.1 AA compliance script for heal-spa.com
 *
 * HOW TO ADD TO SHOPIFY:
 *   1. Upload this file to your theme's Assets folder
 *   2. In theme.liquid, paste just before </body>:
 *        <script src="{{ 'heal-spa-ada-fixes.js' | asset_url }}" defer></script>
 *
 * Issues fixed in this file
 * ─────────────────────────
 *  FIX-01  Google Maps iframe — missing title attribute
 *  FIX-02  Mailchimp mc-iframe — missing title attribute
 *  FIX-03  Autoplay video — no pause mechanism (WCAG 2.2.2)
 *  FIX-04  Autoplay video — paused automatically when user prefers reduced-motion
 *  FIX-05  Mobile nav drawer back-arrow buttons — icon-only, no accessible name
 *  FIX-06  Product cards — duplicate same-destination links; image link hidden
 *          from keyboard/AT, name link remains as the single tab stop
 *  FIX-07  Cart icon link — badge count announced but cart link needs label
 *  FIX-08  Logo link — no accessible name beyond the image alt text
 *  FIX-09  Product images with empty alt="" that carry visual content
 *  FIX-10  Filter sidebar — wrap in labeled <aside> landmark
 *  FIX-11  "Sort by" select missing visible label association on mobile
 *  FIX-12  Announcement bar — live-region for dynamic content updates
 *  FIX-13  Newsletter/email modal focus trap — ensure keyboard cannot escape
 *  FIX-14  "10% off" snackbar close button — ensure consistent aria-label
 *  FIX-15  SVG icons in interactive elements — add aria-hidden where missing
 *  FIX-16  Positive tabindex audit — warn in console if any found (none expected)
 *  FIX-17  After-page-load mutation observer — re-run fixes on dynamic content
 *          (e.g., cart drawer, search results) as Shopify injects it
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────
     Utility helpers
     ───────────────────────────────────────────────────────────────────── */

  /** Set an attribute only if the element exists and doesn't already have it */
  function setAttrIfMissing(el, attr, value) {
    if (el && !el.hasAttribute(attr)) {
      el.setAttribute(attr, value);
    }
  }

  /** Query all matching elements — returns Array (never null) */
  function qsAll(selector, root) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  /** Clamp focus inside a container; returns the teardown function */
  function trapFocus(container, onEscape) {
    const focusable = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    function getEls() {
      return Array.from(container.querySelectorAll(focusable)).filter(
        (el) => !el.closest('[hidden]') && getComputedStyle(el).display !== 'none'
      );
    }

    function handleKeyDown(e) {
      if (e.key === 'Escape' && typeof onEscape === 'function') {
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      const els = getEls();
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true);
    return function teardown() {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-01  Google Maps iframe — title
     ───────────────────────────────────────────────────────────────────── */
  function fixMapIframe() {
    qsAll('iframe[src*="google.com/maps"]').forEach(function (iframe) {
      setAttrIfMissing(iframe, 'title', 'Map showing Heal Spa location');
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-02  Mailchimp mc-iframe — title
     ───────────────────────────────────────────────────────────────────── */
  function fixMailchimpIframe() {
    var mc = document.querySelector('iframe[name="mc-iframe"]');
    setAttrIfMissing(mc, 'title', 'Newsletter subscription form');
    // Also hide from AT; it's purely a POST target, not interactive content
    if (mc) mc.setAttribute('aria-hidden', 'true');
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-03 / FIX-04  Autoplay video — pause control + reduced-motion
     ───────────────────────────────────────────────────────────────────── */
  function fixAutoplayingVideos() {
    qsAll('video[autoplay]').forEach(function (video) {
      // FIX-04: immediately pause if user prefers reduced motion
      var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) {
        video.pause();
        video.removeAttribute('autoplay');
      }

      // FIX-03: add a visible, keyboard-accessible pause/play button
      var wrapper = video.parentElement;
      if (!wrapper) return;

      // Make sure wrapper is positioned so the button can be absolutely placed
      var wrapperPos = getComputedStyle(wrapper).position;
      if (wrapperPos === 'static') wrapper.style.position = 'relative';

      // Don't add a second button if already present
      if (wrapper.querySelector('.hs-video-pause-btn')) return;

      var btn = document.createElement('button');
      btn.className = 'hs-video-pause-btn';
      btn.type = 'button';
      btn.setAttribute('aria-label', video.paused ? 'Play background video' : 'Pause background video');
      btn.textContent = video.paused ? '▶ Play' : '⏸ Pause';

      btn.addEventListener('click', function () {
        if (video.paused) {
          video.play();
          btn.textContent = '⏸ Pause';
          btn.setAttribute('aria-label', 'Pause background video');
        } else {
          video.pause();
          btn.textContent = '▶ Play';
          btn.setAttribute('aria-label', 'Play background video');
        }
      });

      wrapper.appendChild(btn);

      // Decorative background videos should be hidden from AT
      video.setAttribute('aria-hidden', 'true');
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-05  Mobile drawer back-arrow buttons — aria-label
     Back buttons have visible text like "SHOP" but no aria-label; the SVG
     arrow icon before the text can confuse screen readers. We confirm the
     accessible name is the visible text and add it explicitly.
     ───────────────────────────────────────────────────────────────────── */
  function fixDrawerBackButtons() {
    qsAll('.menu-drawer__close-button').forEach(function (btn) {
      if (btn.hasAttribute('aria-label')) return; // already labelled

      // Collect the visible text (strip SVG content)
      var clone = btn.cloneNode(true);
      clone.querySelectorAll('svg, .svg-wrapper').forEach(function (n) { n.remove(); });
      var text = clone.textContent.trim();

      if (text) {
        btn.setAttribute('aria-label', 'Go back to ' + text + ' menu');
      } else {
        btn.setAttribute('aria-label', 'Close submenu');
      }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-06  Product card duplicate links
     Each product card renders TWO <a> tags with the same href:
       1. .card__media a.full-unstyled-link  (wraps the product image)
       2. .card__information a.full-unstyled-link  (wraps the product name)
     Both have aria-labelledby pointing to the same IDs. Screen readers
     announce the product name twice and keyboard users hit two tab stops
     for one destination.
     Fix: set aria-hidden="true" and tabindex="-1" on the image link so
     only the name link remains in the tab order and AT reading order.
     ───────────────────────────────────────────────────────────────────── */
  function fixProductCardLinks() {
    qsAll('.card-wrapper').forEach(function (card) {
      // The image link — sits inside .card__media
      var imageLink = card.querySelector('.card__media .full-unstyled-link, .card__media > a');
      // The name/text link — sits inside .card__information or the card content
      var nameLink = card.querySelector('.card__content .full-unstyled-link, .card__information a, h3 a, .card__heading a');

      if (imageLink && nameLink && imageLink !== nameLink) {
        imageLink.setAttribute('aria-hidden', 'true');
        imageLink.setAttribute('tabindex', '-1');
        imageLink.style.pointerEvents = 'none'; // mouse clicks handled by card wrapper
      }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-07  Cart icon link — accessible label with count
     The cart link shows a count badge visually but the link's accessible
     name may just be the SVG icon. We add an aria-label that includes
     the item count so screen readers announce "Cart (3 items)".
     ───────────────────────────────────────────────────────────────────── */
  function fixCartLink() {
    var cartLinks = qsAll('a[href="/cart"]');
    cartLinks.forEach(function (link) {
      // Find the count badge if present
      var badge = link.querySelector('.cart-count-bubble, [data-cart-count]');
      var count = badge ? (parseInt(badge.textContent.trim(), 10) || 0) : 0;
      var label = count > 0
        ? 'Cart (' + count + (count === 1 ? ' item' : ' items') + ')'
        : 'Cart';
      link.setAttribute('aria-label', label);
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-08  Logo link — accessible name
     The header logo is an <a> wrapping an <img>. The img already has a
     descriptive alt. We also ensure the link itself carries an aria-label
     so context ("go to homepage") is explicit.
     ───────────────────────────────────────────────────────────────────── */
  function fixLogoLink() {
    var logoLinks = qsAll('.header__heading-link, .header__heading a');
    logoLinks.forEach(function (link) {
      setAttrIfMissing(link, 'aria-label', 'Heal Spa — Go to homepage');
    });

    // Also ensure logo images that have empty alt get a meaningful one
    qsAll('.header__heading-link img, .header__heading a img').forEach(function (img) {
      if (!img.getAttribute('alt') || img.getAttribute('alt') === '') {
        img.setAttribute('alt', 'Heal Spa logo');
      }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-09  Section images with empty alt that are not purely decorative
     Images that are the primary visual of a content section (hero,
     feature sections) should have meaningful alt text. We can't
     auto-generate accurate descriptions, so we flag images that:
       - have alt=""
       - are NOT inside .card__media (product images – alt="" is valid there
         since the card link provides the name)
       - are larger than 200×200 px (clearly content images)
     We give them a generic fallback alt and log a warning.
     ───────────────────────────────────────────────────────────────────── */
  function fixContentImages() {
    qsAll('img[alt=""]').forEach(function (img) {
      // Skip product-card images — their accessible name comes from the link
      if (img.closest('.card__media') || img.closest('.product-card-wrapper')) return;
      // Skip tiny images (icons, spacers)
      if (img.naturalWidth > 0 && img.naturalWidth < 50) return;
      if (img.width && img.width < 50) return;

      // For section/hero images give a meaningful role
      // If truly decorative, the theme should add role="presentation"
      // We set a fallback alt so screen readers don't announce the filename
      var src = img.getAttribute('src') || '';
      var filename = src.split('/').pop().split('?')[0].replace(/[-_]/g, ' ').replace(/\.\w+$/, '');
      img.setAttribute('alt', 'Heal Spa — ' + filename);

      console.warn('[heal-spa-ada] Image with empty alt (non-decorative context). Consider adding a meaningful alt text:', img.src);
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-10  Filter sidebar — landmark role
     The facet/filter sidebar on collection pages has no landmark role,
     so keyboard and screen-reader users cannot jump directly to it.
     We wrap it (or add a role) to expose it as a complementary landmark.
     ───────────────────────────────────────────────────────────────────── */
  function fixFilterSidebar() {
    var filterForms = qsAll('.facets__form, form[id*="facet"], .facets-vertical');
    filterForms.forEach(function (form) {
      // Look for the wrapping section/div
      var wrapper = form.closest('aside, [role="complementary"]');
      if (!wrapper) {
        // Walk up to find a reasonable container and landmark it
        var candidate = form.closest('.facets-vertical, .facets-container, .filter-container');
        if (!candidate) candidate = form.parentElement;
        if (candidate && !candidate.hasAttribute('role')) {
          candidate.setAttribute('role', 'complementary');
          setAttrIfMissing(candidate, 'aria-label', 'Product filters');
        }
      }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-11  Sort-by select — visible label on mobile
     The mobile sort select (#SortBy-mobile) has a <label for="SortBy">
     pointing to the desktop select, not the mobile one. Fix the
     association.
     ───────────────────────────────────────────────────────────────────── */
  function fixSortSelect() {
    var mobileSelect = document.getElementById('SortBy-mobile');
    if (mobileSelect) {
      // If no label points at it, create a visually-hidden one
      var existingLabel = document.querySelector('label[for="SortBy-mobile"]');
      if (!existingLabel) {
        var label = document.createElement('label');
        label.setAttribute('for', 'SortBy-mobile');
        label.className = 'visually-hidden';
        label.textContent = 'Sort products by';
        mobileSelect.parentElement.insertBefore(label, mobileSelect);
      }
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-12  Announcement bar — live region
     The announcement bar may rotate messages dynamically. Mark it as an
     aria-live region so screen readers announce updates automatically.
     ───────────────────────────────────────────────────────────────────── */
  function fixAnnouncementBar() {
    var bars = qsAll('.announcement-bar, [aria-label="Announcement"]');
    bars.forEach(function (bar) {
      setAttrIfMissing(bar, 'aria-live', 'polite');
      setAttrIfMissing(bar, 'aria-atomic', 'true');
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-13  Email popup — focus trap
     The #email-modal popup already has role="dialog" and aria-modal="true"
     (added by a previous customization). We reinforce the focus trap in
     case it was not fully wired or is re-rendered by Shopify.
     ───────────────────────────────────────────────────────────────────── */
  var emailModalTeardown = null;

  function fixEmailModal() {
    var modal = document.getElementById('email-modal');
    if (!modal) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          var isVisible = modal.style.display !== 'none' && modal.offsetParent !== null;
          if (isVisible) {
            openEmailModal(modal);
          } else {
            closeEmailModal();
          }
        }
      });
    });

    observer.observe(modal, { attributes: true });
  }

  function openEmailModal(modal) {
    if (emailModalTeardown) emailModalTeardown();
    // Move focus into the modal
    var firstFocusable = modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) firstFocusable.focus();

    emailModalTeardown = trapFocus(modal, function () {
      // Close the modal on Escape
      var closeBtn = modal.querySelector('[aria-label="Close popup"], .popup-close, button[onclick*="closeModal"]');
      if (closeBtn) closeBtn.click();
    });
  }

  function closeEmailModal() {
    if (emailModalTeardown) {
      emailModalTeardown();
      emailModalTeardown = null;
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-14  Snackbar "10% off" close button
     ───────────────────────────────────────────────────────────────────── */
  function fixSnackbarClose() {
    // Look for the dismiss × button in the snackbar / popup-bar
    qsAll('button, a').forEach(function (el) {
      var text = el.textContent.trim();
      var isCloseSymbol = text === '×' || text === '✕' || text === '✖' || text === 'x';
      var hasLabel = el.hasAttribute('aria-label');
      var isInSnackbar = el.closest('[class*="snackbar"], [id*="snackbar"], [class*="popup-bar"], [id*="popup-bar"], [class*="discount"], [id*="discount"]');
      if (isCloseSymbol && !hasLabel && isInSnackbar) {
        el.setAttribute('aria-label', 'Dismiss offer');
      }
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-15  SVG icons inside interactive elements — aria-hidden
     Inline SVG icons inside buttons and links should be hidden from AT
     so the button's text content / aria-label is the only thing announced.
     ───────────────────────────────────────────────────────────────────── */
  function fixSvgIcons() {
    qsAll('button svg, a svg, [role="button"] svg').forEach(function (svg) {
      setAttrIfMissing(svg, 'aria-hidden', 'true');
      setAttrIfMissing(svg, 'focusable', 'false'); // IE11 fix
    });

    // Also hide wrapper spans that contain only an SVG
    qsAll('button .svg-wrapper, a .svg-wrapper').forEach(function (span) {
      setAttrIfMissing(span, 'aria-hidden', 'true');
    });
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-16  Positive tabindex audit (dev helper — not user facing)
     ───────────────────────────────────────────────────────────────────── */
  function auditPositiveTabindex() {
    var found = qsAll('[tabindex]').filter(function (el) {
      return parseInt(el.getAttribute('tabindex'), 10) > 0;
    });
    if (found.length) {
      console.warn('[heal-spa-ada] ' + found.length + ' element(s) have positive tabindex — this disrupts natural tab order:', found);
    }
  }


  /* ─────────────────────────────────────────────────────────────────────
     FIX-17  MutationObserver — re-run fixes on dynamic Shopify content
     Cart drawer, search results, and quick-add modals are injected by JS
     after initial load. We observe the document body for added nodes and
     re-run the relevant fixers.
     ───────────────────────────────────────────────────────────────────── */
  function setupMutationObserver() {
    var debounceTimer;
    var observer = new MutationObserver(function (mutations) {
      var hasNewNodes = mutations.some(function (m) { return m.addedNodes.length > 0; });
      if (!hasNewNodes) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        fixProductCardLinks();
        fixCartLink();
        fixSvgIcons();
        fixDrawerBackButtons();
        fixAutoplayingVideos();
        fixMapIframe();
      }, 150);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }


  /* ─────────────────────────────────────────────────────────────────────
     Bootstrap — run all fixes once the DOM is ready
     ───────────────────────────────────────────────────────────────────── */
  function runAllFixes() {
    fixMapIframe();
    fixMailchimpIframe();
    fixAutoplayingVideos();
    fixDrawerBackButtons();
    fixProductCardLinks();
    fixCartLink();
    fixLogoLink();
    fixContentImages();
    fixFilterSidebar();
    fixSortSelect();
    fixAnnouncementBar();
    fixEmailModal();
    fixSnackbarClose();
    fixSvgIcons();
    auditPositiveTabindex();
    setupMutationObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAllFixes);
  } else {
    // DOM is already ready (script has defer attribute)
    runAllFixes();
  }

})();
